import polyglotI18nProvider from 'ra-i18n-polyglot';
import {
    Admin,
    CustomRoutes,
    Resource,
    localStorageStore,
    useStore,
    StoreContextProvider,
} from 'react-admin';
import { Route } from 'react-router';

import authProvider from './authProvider';
import { Dashboard } from './dashboard';
import dataProviderFactory from './dataProvider';
import englishMessages from './i18n/en';
import { Layout, Login } from './layout';
import chats from './chats';
import aiVideo from './aiVideo';
import assessment from './assessment';
import kivoaiVideo from './kivoaiVideo';
import subscriptions from './subscriptions';
import aiReport from './aiReport';
import { adminUsers, clubUsers, coachUsers, playerUsers } from './userManagement';
import { themes, ThemeName } from './themes/themes';

const i18nProvider = polyglotI18nProvider(
    locale => {
        if (locale === 'fr') {
            return import('./i18n/fr').then(messages => messages.default);
        }

        // Always fallback on english
        return englishMessages;
    },
    'en',
    [
        { locale: 'en', name: 'English' },
        { locale: 'fr', name: 'Français' },
    ]
);

const store = localStorageStore(undefined, 'ECommerce');

const App = () => {
    const [themeName] = useStore<ThemeName>('themeName', 'soft');
    const singleTheme = themes.find(theme => theme.name === themeName)?.single;
    const lightTheme = themes.find(theme => theme.name === themeName)?.light;
    const darkTheme = themes.find(theme => theme.name === themeName)?.dark;
    return (
        <Admin
            title="Posters Galore Admin"
            dataProvider={dataProviderFactory(
                import.meta.env.VITE_DATA_PROVIDER || 'api'
            )}
            store={store}
            authProvider={authProvider}
            dashboard={Dashboard}
            loginPage={Login}
            layout={Layout}
            i18nProvider={i18nProvider}
            disableTelemetry
            theme={singleTheme}
            lightTheme={lightTheme}
            darkTheme={darkTheme}
            defaultTheme="light"
            requireAuth
        >
            <Resource name="admin/chats" {...chats} options={{ label: 'Chats' }} />
            <Resource name="admin/user-management/player-users" {...playerUsers} options={{ label: 'Players' }} />
            <Resource name="admin/user-management/club-users" {...clubUsers} options={{ label: 'Clubs' }} />
            <Resource name="admin/user-management/coach-users" {...coachUsers} options={{ label: 'Coaches' }} />
            <Resource name="admin/user-management/admin-users" {...adminUsers} options={{ label: 'Admins' }} />
            <Resource name="admin/aiVideo" {...aiVideo} options={{ label: 'AI Videos' }} />
            <Resource
                name="admin/assessment"
                {...assessment}
                options={{ label: 'Assessment' }}
            />
            <Resource name="admin/ai-reports" {...aiReport} options={{ label: 'AI Reports' }} />
            <Resource name="admin/kivoai-videos" {...kivoaiVideo} options={{ label: 'KivoAI Videos' }} />
            <Resource name="admin/user-subscriptions" {...subscriptions} options={{ label: 'User Subscriptions' }} />
        </Admin>
    );
};

const AppWrapper = () => (
    <StoreContextProvider value={store}>
        <App />
    </StoreContextProvider>
);

export default AppWrapper;
